class ChatWebSocket {
  constructor(baseUrl, authToken) {
    this.baseUrl =
      baseUrl || 'wss://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev';
    this.authToken = authToken;
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnecting = false;

    // Event handlers
    this.messageHandlers = {
      onMessage: null,
      onTyping: null,
      onStatusUpdate: null,
      onConnection: null,
      onError: null,
      onConnectionChange: null
    };
  }

  // Set event handlers
  setMessageHandler(handler) {
    this.messageHandlers.onMessage = handler;
  }

  setTypingHandler(handler) {
    this.messageHandlers.onTyping = handler;
  }

  setStatusUpdateHandler(handler) {
    this.messageHandlers.onStatusUpdate = handler;
  }

  setConnectionHandler(handler) {
    this.messageHandlers.onConnection = handler;
  }

  setErrorHandler(handler) {
    this.messageHandlers.onError = handler;
  }

  setConnectionChangeHandler(handler) {
    this.messageHandlers.onConnectionChange = handler;
  }

  connect(organizationId, userId, userType = 'user') {
    if (
      this.isConnecting ||
      (this.ws && this.ws.readyState === WebSocket.OPEN)
    ) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.isConnecting = true;
    const wsUrl = `${this.baseUrl}?organizationId=${organizationId}&userId=${userId}&userType=${userType}&token=${this.authToken}`;

    console.log('🔌 Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      if (this.messageHandlers.onError) {
        this.messageHandlers.onError(error);
      }
    }
  }

  // New v2.0 method for user connections (no organizationId needed)
  connectAsUser(fallbackToLegacy = false) {
    if (
      this.isConnecting ||
      (this.ws && this.ws.readyState === WebSocket.OPEN)
    ) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.isConnecting = true;

    let wsUrl;
    if (fallbackToLegacy) {
      // Try multiple legacy patterns
      const defaultOrgId =
        process.env.REACT_APP_CHATBOT_DEFAULT_ORGANIZATION_ID ||
        '64d613865d69f0f8d29a443a';
      console.log('🔄 Falling back to legacy WebSocket connection patterns');

      // Try the original legacy pattern with /ws path
      wsUrl = `${
        this.baseUrl
      }/ws?organizationId=${defaultOrgId}&userType=user&token=${encodeURIComponent(
        this.authToken
      )}`;
    } else {
      // v2.0 user WebSocket - try multiple patterns
      console.log('🔄 Trying v2.0 WebSocket connection patterns');

      // Try with /ws path first for v2.0
      wsUrl = `${this.baseUrl}/ws?token=${encodeURIComponent(this.authToken)}`;
    }

    console.log(
      `🔌 Connecting to ${
        fallbackToLegacy ? 'Legacy' : 'v2.0'
      } User WebSocket:`,
      {
        wsUrl: wsUrl.replace(
          encodeURIComponent(this.authToken),
          '[TOKEN_HIDDEN]'
        ),
        hasToken: !!this.authToken,
        baseUrl: this.baseUrl,
        fallbackMode: fallbackToLegacy
      }
    );

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();

      // If this is the first attempt (v2.0), set up fallback on failure
      if (!fallbackToLegacy) {
        this.ws.addEventListener('error', () => {
          console.log('🔄 v2.0 WebSocket failed, trying legacy pattern...');
          setTimeout(() => {
            if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
              this.ws = null;
              this.isConnecting = false;
              this.connectAsUser(true); // Retry with legacy pattern
            }
          }, 1000);
        });
      }
    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      if (this.messageHandlers.onError) {
        this.messageHandlers.onError(error);
      }

      // If v2.0 failed, try legacy
      if (!fallbackToLegacy) {
        console.log('🔄 v2.0 connection failed, trying legacy...');
        setTimeout(() => this.connectAsUser(true), 1000);
      }
    }
  }

  // New v2.0 method for agent connections (requires organizationId and agent validation)
  connectAsAgent(organizationId) {
    if (
      this.isConnecting ||
      (this.ws && this.ws.readyState === WebSocket.OPEN)
    ) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.isConnecting = true;
    // v2.0 agent WebSocket - needs organizationId and userType=agent
    const wsUrl = `${this.baseUrl}?token=${this.authToken}&organizationId=${organizationId}&userType=agent`;

    console.log('🔌 Connecting to v2.0 Agent WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      if (this.messageHandlers.onError) {
        this.messageHandlers.onError(error);
      }
    }
  }

  setupEventHandlers() {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.clearReconnectInterval();

      if (this.messageHandlers.onConnectionChange) {
        this.messageHandlers.onConnectionChange(true);
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('📨 Raw WebSocket message received:', {
          rawData: event.data,
          parsedMessage: message,
          messageType: message.type,
          hasData: !!message.data
        });
        this.handleIncomingMessage(message);
      } catch (error) {
        console.error('❌ Failed to parse WebSocket message:', error, {
          rawData: event.data
        });
      }
    };

    this.ws.onclose = (event) => {
      console.log('❌ WebSocket disconnected:', event.code, event.reason);
      this.isConnecting = false;

      if (this.messageHandlers.onConnectionChange) {
        this.messageHandlers.onConnectionChange(false);
      }

      // Only attempt reconnect if it wasn't a manual close (code 1000)
      if (event.code !== 1000) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.isConnecting = false;

      if (this.messageHandlers.onError) {
        this.messageHandlers.onError(error);
      }
    };
  }

  handleIncomingMessage(message) {
    console.log('🔄 Processing WebSocket message:', {
      type: message.type,
      hasHandler: {
        message: !!this.messageHandlers.onMessage,
        typing: !!this.messageHandlers.onTyping,
        statusUpdate: !!this.messageHandlers.onStatusUpdate,
        connection: !!this.messageHandlers.onConnection
      }
    });

    switch (message.type) {
      case 'message':
        console.log('📨 Processing message event:', message.data);
        if (this.messageHandlers.onMessage) {
          this.messageHandlers.onMessage(message.data);
        } else {
          console.warn('❌ No message handler registered');
        }
        break;
      case 'typing':
        console.log('⌨️ Processing typing event:', message.data);
        if (this.messageHandlers.onTyping) {
          this.messageHandlers.onTyping(message.data);
        }
        break;
      case 'status_update':
        console.log('📊 Processing status update event:', message.data);
        if (this.messageHandlers.onStatusUpdate) {
          this.messageHandlers.onStatusUpdate(message.data);
        }
        break;
      case 'connection':
        console.log('🔗 Processing connection event:', message.data);
        if (this.messageHandlers.onConnection) {
          this.messageHandlers.onConnection(message.data);
        }
        break;
      default:
        console.warn(
          '🤷 Unknown WebSocket message type:',
          message.type,
          message
        );
    }
  }

  scheduleReconnect() {
    if (
      this.reconnectInterval ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    console.log(
      `🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`
    );

    this.reconnectInterval = setTimeout(() => {
      this.clearReconnectInterval();
      if (this.reconnectAttempts <= this.maxReconnectAttempts) {
        console.log(
          `🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
        );
        // Re-establish connection with same parameters
        this.reconnect();
      }
    }, delay);
  }

  reconnect() {
    // Implementation would require storing connection parameters
    // For now, let the component handle reconnection
    if (this.messageHandlers.onConnectionChange) {
      this.messageHandlers.onConnectionChange(false, true); // false = disconnected, true = should reconnect
    }
  }

  clearReconnectInterval() {
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  // Send message via WebSocket
  sendMessage(conversationId, content, type = 'outgoing') {
    if (!this.isConnected()) {
      console.error('❌ WebSocket not connected, cannot send message');
      return false;
    }

    const message = {
      action: 'sendMessage',
      conversationId: conversationId,
      content: content,
      type: type
    };

    try {
      this.ws.send(JSON.stringify(message));
      console.log('📤 WebSocket message sent:', message);
      return true;
    } catch (error) {
      console.error('❌ Failed to send WebSocket message:', error);
      return false;
    }
  }

  // Send message via WebSocket (v2.0 - simplified for users, content and conversationId only)
  sendUserMessage(conversationId, content) {
    if (!this.isConnected()) {
      console.error('❌ WebSocket not connected, cannot send message');
      return false;
    }

    const message = {
      action: 'sendMessage',
      conversationId: conversationId,
      content: content
      // type and senderId automatically handled by backend for users
    };

    try {
      this.ws.send(JSON.stringify(message));
      console.log('📤 v2.0 User WebSocket message sent:', message);
      return true;
    } catch (error) {
      console.error('❌ Failed to send v2.0 user WebSocket message:', error);
      return false;
    }
  }

  // Send typing indicator
  sendTyping(conversationId, isTyping = true) {
    if (!this.isConnected()) {
      return false;
    }

    const message = {
      action: 'typing',
      conversationId: conversationId,
      isTyping: isTyping
    };

    try {
      this.ws.send(JSON.stringify(message));
      console.log('⌨️ Typing indicator sent:', message);
      return true;
    } catch (error) {
      console.error('❌ Failed to send typing indicator:', error);
      return false;
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  disconnect() {
    this.clearReconnectInterval();
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect

    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    console.log('🔌 WebSocket manually disconnected');
  }

  // Get connection status
  getConnectionStatus() {
    if (!this.ws) return 'disconnected';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }
}

export default ChatWebSocket;
